import React from 'react';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { FormatCurrency } from '@css-xp/ppc-common';
import { fetchInvoicesByEntitlementIdPPC } from '../queries/fetch-invoices-by-entitlement-id';

interface props {
  entitlementId?: string;
  transactionAccountId: string;
  isRightAligned?: boolean;
}

export const LastInvoiceAmountCell = ({ entitlementId, transactionAccountId, isRightAligned }: props) => {
  const { data, loading, error } = useQuery<
    generatedTypesNew.FetchInvoicesByEntitlementIdPpcQuery,
    generatedTypesNew.FetchInvoicesByEntitlementIdPpcQueryVariables
  >(fetchInvoicesByEntitlementIdPPC, {
    // Note: The default sorting is by `createdDate` in `SortDirection.DESC`, which is what is needed here.
    variables: {
      searchOptions: {
        transactionAccountId,
        entitlementId: entitlementId,
        size: 1,
        includeItems: true, // To include line items
      },
    },
    skip: !entitlementId,
    nextFetchPolicy: 'cache-and-network',
  });
  const invoiceResults = data?.invoices?.results?.[0];
  const currency = invoiceResults?.pricingCurrency ?? invoiceResults?.paymentCurrency;

  if (loading) {
    return (
      <td>
        <SkeletonLoaderWrapper>
          <SkeletonLoader width="60%" marginBottom="0" />
        </SkeletonLoaderWrapper>
      </td>
    );
  }

  if (error || !entitlementId || !currency) {
    return (
      <td data-testid="no-invoice-data" className={classNames({ 'text-align-right': isRightAligned })}>
        -
      </td>
    );
  }

  let amount = 0;
  invoiceResults?.lineItems?.forEach((item: any) => {
    if (item.entitlementId === entitlementId) {
      amount += parseFloat(item.amount);
    }
  });

  return (
    <td className={classNames({ 'text-align-right': isRightAligned })}>
      <FormatCurrency value={amount} currency={currency} showCurrencyCode showCommas />
    </td>
  );
};
